import React, { useCallback, useMemo } from 'react';
import { ITEMS_LIST_QUERY, ITEM_TYPES_QUERY } from '../queries';
import { PROPERTIES_QUERY } from 'features/properties/components/list/queries';
import { ITEM_REMOVED } from '../../edit/subscriptions';
import { useTranslation } from 'react-i18next';
import { Card } from '@totalsoft/rocket-ui';
import { CollectionType, getListBrowserStore } from '@ts-private/list-browser';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { ExportModalComponent } from 'components/common/export/ExportModalComponent';
import { useToast } from 'hooks/toasts/useToast';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import { useMutation } from 'hooks/apollo/useMutation';
import { DELETE_ITEM } from 'features/items/edit/mutations';
import ItemComponent from './ItemComponent';
import useSubscription from 'hooks/apollo/useSubscription';

export const itemsStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'code',
      caption: 'Items.Code',
      type: 'text'
    },
    {
      name: 'name',
      caption: 'Items.Name',
      type: 'text'
    },
    {
      name: 'typeId',
      caption: 'Items.Type',
      type: 'select',
      underlyingType: 'number'
    },
    {
      name: 'enabled',
      caption: 'Items.Enabled',
      type: 'boolean'
    },
    {
      name: 'propertyId',
      caption: 'Items.Property',
      type: 'select',
      underlyingType: 'number'
    },
    {
      name: 'propertyValue',
      caption: 'Items.PropertyValue',
      type: 'text'
    }
  ],
  orderByFields: [
    {
      caption: 'Items.Name',
      value: 'ItemName'
    },
    {
      caption: 'Items.Code',
      value: 'ItemCode'
    },
    {
      caption: 'Items.Type',
      value: 'ItemType'
    }
  ],
  pager: {
    orderBy: 'ItemName',
    direction: 'asc'
  }
});

const ItemsListBrowser = () => {
  const confirm = useConfirmDialog();
  const addToast = useToast();
  const [t] = useTranslation();

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: ITEMS_LIST_QUERY,
        name: 'items'
      },
      collectionsOptions: [
        {
          queryOptions: { query: ITEM_TYPES_QUERY },
          name: 'itemTypes',
          identifier: 'typeId',
          collectionType: CollectionType.Filter,
          valueKey: 'id',
          labelKey: 'name'
        },
        {
          queryOptions: { query: PROPERTIES_QUERY },
          name: 'properties',
          identifier: 'propertyId',
          collectionType: CollectionType.Filter,
          valueKey: 'propertyId',
          labelKey: 'propertyName'
        }
      ]
    }),
    []
  );

  useSubscription(ITEM_REMOVED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('Items.ItemRemoved'), 'success');
      client.cache.evict({ id: `Item:{"itemId":${data.itemRemoved.itemId}}` });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'items' });
      client.cache.gc();
    }
  });

  const [deleteItem] = useMutation(DELETE_ITEM);

  const handleDelete = useCallback(
    itemId => {
      confirm(
        () => {
          deleteItem({ variables: { input: { itemId } } });
        },
        {
          content: t('Items.DeleteItem')
        }
      );
    },
    [deleteItem, confirm, t]
  );

  const itemComponent = useCallback(({ data }) => <ItemComponent data={data} onDelete={handleDelete} />, [handleDelete]);

  const columns = useMemo(
    () => [
      { name: 'code', caption: 'Items.Code' },
      { name: 'name', caption: 'Items.Name' },
      { name: 'typeCode', caption: 'Items.TypeCode' },
      { name: 'typeName', caption: 'Items.TypeName' },
      { name: 'enabled', caption: 'Items.Enabled', width: 10, align: 'center' },
      { name: 'actions', caption: 'Actions', width: '120px' }
    ],
    []
  );

  return (
    <Card icon={ListAltIcon}>
      <TableListBrowser name='items-browser' store={itemsStore} ItemComponent={itemComponent} columns={columns} />
      <ExportModalComponent store={itemsStore} options={fetchingOptions} />
      <ApolloFetchingComponent store={itemsStore} options={fetchingOptions} />
    </Card>
  );
};

export default ItemsListBrowser;
